import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../shared/ThemedChartTooltip";
import InfoTip from "../../shared/InfoTip";

/**
 * GroupedBarChart — shared evidence component for site_type_response
 * and market_tolerance.
 *
 * Grouped bars per category/market: dry forecast vs wet forecast.
 * Legend: "Dry forecast" / "Rain forecast".
 *
 * site_type_response: dry_forecast_bookings vs wet_forecast_bookings
 * market_tolerance: conversion_dry_pct vs conversion_wet_pct
 *   - Grey wet bars where wet_sessions < 100
 *
 * Spec references: §7 (site_type_response, market_tolerance).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

/** Chart title based on panel key. */
const getChartTitle = (panelKey) => {
  if (panelKey === "market_tolerance") return "Conversion by Origin Market";
  return "Bookings by Site Type";
};

/** InfoTip text based on panel key. */
const getInfoTipText = (panelKey) => {
  if (panelKey === "market_tolerance") {
    return "Conversion rate for each origin market on dry vs rain-forecast days. Greyed wet bars have fewer than 100 wet-forecast sessions and are unreliable.";
  }
  return "Bookings for each site type on dry vs rain-forecast days. The split shows which accommodation types hold up when rain is in the forecast.";
};

const GroupedBarChart = ({ panel }) => {
  const f = panel.facts || {};
  const chartTitle = getChartTitle(panel.panel);
  const infoTipText = getInfoTipText(panel.panel);

  // ─── site_type_response ───────────────────────────────────────────────────
  if (panel.panel === "site_type_response") {
    const categories = f.categories || [];
    if (categories.length === 0) return null;

    const chartData = categories.map((c) => ({
      name: c.site_category,
      "Dry forecast": c.dry_forecast_bookings || 0,
      "Rain forecast": c.wet_forecast_bookings || 0,
    }));

    return (
      <div className="space-y-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <span>{chartTitle}</span>
              <InfoTip text={infoTipText} />
            </h3>
            {f.window_days && f.sessions != null && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Last {f.window_days} days · {fmtNum(f.sessions)} visits
              </p>
            )}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={{ stroke: "#374151" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                width={45}
              />
              <ThemedChartTooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const dry = payload.find((p) => p.dataKey === "Dry forecast");
                  const wet = payload.find((p) => p.dataKey === "Rain forecast");
                  return (
                    <ThemedTooltipBox title={label}>
                      <div>Dry forecast: {fmtNum(dry?.value)} bookings</div>
                      <div>Rain forecast: {fmtNum(wet?.value)} bookings</div>
                    </ThemedTooltipBox>
                  );
                }}
              />
              <Bar dataKey="Dry forecast" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Bar dataKey="Rain forecast" fill="#93C5FD" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>

          {/* Legend — centered below the graph */}
          <div className="flex items-center justify-center gap-4 text-[11px] text-gray-500 dark:text-gray-400 mt-3">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-blue-500" />
              Dry forecast
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-blue-300" />
              Rain forecast
            </span>
          </div>

          {f.means && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center italic mt-3">
              {f.means}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ─── market_tolerance ─────────────────────────────────────────────────────
  // Boxed chart (like ExpectedVsActual): card with a title + InfoTip header,
  // a left-side FYI sidebar (legend + small-sample caveat + means), and the
  // grouped bar chart on the right. Market names are slanted to fit long labels.
  if (panel.panel === "market_tolerance") {
    const markets = f.markets || [];
    if (markets.length === 0) return null;

    const chartData = markets.map((m) => ({
      name: m.location,
      "Dry forecast": m.conversion_dry_pct ?? 0,
      "Rain forecast": m.conversion_wet_pct ?? 0,
      lowWet: (m.wet_sessions || 0) < 100,
    }));

    const hasLowWet = chartData.some((d) => d.lowWet);

    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        {/* ─── Card header: title + InfoTip (left), window/sessions (right) ── */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <span>{chartTitle}</span>
            <InfoTip text={infoTipText} />
          </h3>
          {f.window_days && f.sessions != null && (
            <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Last {f.window_days} days · {fmtNum(f.sessions)} visits
            </p>
          )}
        </div>

        {/* ─── Body: FYI sidebar (left on lg, top on mobile) + chart ─────── */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* FYI sidebar */}
          <aside className="lg:w-44 flex-shrink-0 space-y-3 lg:border-r lg:border-gray-200 dark:lg:border-gray-800 lg:pr-4">
            <div className="space-y-2 text-[11px] text-gray-500 dark:text-gray-400">
              <p className="font-semibold text-gray-600 dark:text-gray-300">Legend</p>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-blue-500" />
                Dry forecast
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-blue-300" />
                Rain forecast
              </span>
            </div>

            {hasLowWet && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-snug">
                Wet bars with fewer than 100 sessions are greyed out.
              </p>
            )}

            {f.means && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 italic leading-snug">
                {f.means}
              </p>
            )}
          </aside>

          {/* Chart */}
          <div className="flex-1 min-w-0 min-h-[240px]">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  axisLine={{ stroke: "#374151" }}
                  tickLine={false}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}%`}
                  width={45}
                />
                <ThemedChartTooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const dry = payload.find((p) => p.dataKey === "Dry forecast");
                    const wet = payload.find((p) => p.dataKey === "Rain forecast");
                    return (
                      <ThemedTooltipBox title={label}>
                        <div>Dry forecast: {dry?.value}% conversion</div>
                        <div>Rain forecast: {wet?.value}% conversion</div>
                      </ThemedTooltipBox>
                    );
                  }}
                />
                <Bar dataKey="Dry forecast" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={32} />
                <Bar dataKey="Rain forecast" fill="#93C5FD" radius={[4, 4, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default GroupedBarChart;
